
import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { useLoadingMessage } from "../../../components/useLoadingMessage";
import { PageBody } from "../../../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store/rootReducer";
import { useAlert } from "react-alert";
import { seasonThunk } from "../../../redux/action/season/season.thunk";
import { HiDotsVertical } from "react-icons/hi";
import { seasonService } from "../../../Services";
import Layout from "../../../components/Layout";
import { AuthContext } from "../../../context/authContext";
import { roless } from "../../../Constants";
import { useWarehouse } from "./WarehouseContext";

const WarehouseManagerComponent = () => {
  const { role } = useContext(AuthContext);
  const { isLoading, message, updateLoader } = useLoadingMessage();
  const dispatch = useDispatch();
  const alert = useAlert();
  const [warehouse, setWarehouse] = React.useState<any>([]);
  const [warehouseCount, setWarehouseCount] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 6; 
  const { setSelectedWarehouse, setAllWarehouses } = useWarehouse();

  const getWarehouse = async () => {
    updateLoader(true, "Fetching Warehouses");
    try {
      const response = await seasonService.getAllWerehouse();
      console.log("Full Response:", response);

      if (response?.data?.data?.warehouseData) {
        const warehouses = response.data.data.warehouseData;
        setWarehouse(warehouses);
        setAllWarehouses(warehouses); 
        setWarehouseCount(response.data.data.warehouses || 0);
      } else {
        console.error(
          "Warehouse data not found in the expected structure:",
          response.data
        );
        alert.error("Failed to load warehouse data");
      }

      updateLoader(false, "");
    } catch (error) {
      console.error("Error fetching warehouses:", error);
      alert.error("An error occurred while fetching warehouses");
      updateLoader(false, "");
    }
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const totalPages = Math.ceil(warehouse.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentWarehouses = warehouse.slice(indexOfFirstItem, indexOfLastItem);

  React.useEffect(() => {
    getWarehouse();
  }, []);

  return (
    <PageBody isLoading={isLoading} message={message}>
      <Layout active="Warehouses">
        <Container>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="mb-0">Warehouses ({warehouseCount})</h3>
            {(roless.BusinessAd === role || roless.ITAd === role) && (
              <Link to="/add-warehouse">
                <Button color="success" size="sm">
                  Add Warehouse
                </Button>
              </Link>
            )}
          </div>
          <Row>
            {currentWarehouses.length < 1 ? (
              <div className="text-center m-auto">
                <h1>No warehouses found</h1>
              </div>
            ) : (
              currentWarehouses.map((item: any, i: number) => (
                <Col sm={6} lg={4} className="mb-3 mt-4" key={item.uniqueId}>
                  <Card className="border-0 shadow season-card h-100">
                    <CardBody>
                      {roless.WarehouseManager && (
                        <div className="float-right bg-gray rounded-circle shadow drop-down-circle">
                          <UncontrolledDropdown className="m-drop">
                            <DropdownToggle className="m-drop bg-transparent border-0 text-dark font-weight-bold">
                              <HiDotsVertical />
                            </DropdownToggle>
                            <DropdownMenu
                              container="body"
                              className="border-0 shadow"
                            >
                              <Link to={`/edit-warehouse/${item.uniqueId}`}>
                                <DropdownItem>Edit</DropdownItem>
                              </Link>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </div>
                      )}
                      <div className="mb-3">
                        <span className="bg-green p-2 text-white">
                          {/* Icon placeholder */}
                        </span>
                        <span className="font-weight-bold">
                          {" "}
                          <span className="truncate">
                            {item["werehouse name"]}
                          </span>
                          <br />
                          <small className="season-year">{item.address}</small>
                          <br />
                          <small className="text-muted">
                            {item.lga}, {item["warehouse state"]}
                          </small>
                        </span>
                      </div>

                      <div>
                      
                        <Link to={`/manage-warehouse/${item["state id"]}/${item.uniqueId}`}>
        
                          <Button
                            block
                            color="success"
                            size="sm"
                            className="font-bold p-2 mt-4"
                            onClick={() => {
                              console.log("Setting selected warehouse:", item);
                              setSelectedWarehouse(item);
                              // Store in localStorage as a backup
                              localStorage.setItem('selectedWarehouse', JSON.stringify(item));
                            }}
                          >
                            Manage
                          </Button>
                        </Link>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))
            )}
          </Row>

          {totalPages > 1 && (
            <div className="mt-4 d-flex justify-content-center">
              <Pagination aria-label="Warehouse pagination">
                <PaginationItem disabled={currentPage === 1}>
                  <PaginationLink first onClick={() => handlePageChange(1)} />
                </PaginationItem>
                <PaginationItem disabled={currentPage === 1}>
                  <PaginationLink
                    previous
                    onClick={() => handlePageChange(currentPage - 1)}
                  />
                </PaginationItem>

                {[...Array(totalPages)].map((_, i) => (
                  <PaginationItem key={i} active={currentPage === i + 1}>
                    <PaginationLink onClick={() => handlePageChange(i + 1)}>
                      {i + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}

                <PaginationItem disabled={currentPage === totalPages}>
                  <PaginationLink
                    next
                    onClick={() => handlePageChange(currentPage + 1)}
                  />
                </PaginationItem>
                <PaginationItem disabled={currentPage === totalPages}>
                  <PaginationLink
                    last
                    onClick={() => handlePageChange(totalPages)}
                  />
                </PaginationItem>
              </Pagination>
            </div>
          )}
        </Container>
      </Layout>
    </PageBody>
  );
};

export default WarehouseManagerComponent;